import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  Container,
  Link,
  Paper,
  Grid,
  Typography,
  Divider,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { register } from '../store/slices/authSlice';
import { toast } from 'react-toastify';

import Button from '../components/common/Button';
import TextField from '../components/common/TextField';
import Loading from '../components/common/Loading';

const Register = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);

  const {
    register: registerForm,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    try {
      // Format the data to match backend requirements
      const formattedData = {
        ...data,
        address: {
          street: data.street,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode,
          country: data.country,
        },
      };
      await dispatch(register(formattedData)).unwrap();
      toast.success('Registration successful!');
      navigate('/login');
    } catch (error) {
      toast.error(error.message || 'Registration failed');
    }
  };

  if (loading) {
    return <Loading message="Registering..." />;
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5" gutterBottom>
            Sign Up
          </Typography>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label="Full Name"
              name="name"
              autoComplete="name"
              autoFocus
              {...registerForm('name', {
                required: 'Name is required',
                minLength: {
                  value: 2,
                  message: 'Name must be at least 2 characters',
                },
              })}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              {...registerForm('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="mobileNumber"
              label="Mobile Number"
              id="mobileNumber"
              autoComplete="tel"
              {...registerForm('mobileNumber', {
                required: 'Mobile number is required',
                pattern: {
                  value: /^[0-9]{10}$/,
                  message: 'Mobile number must be 10 digits',
                },
              })}
              error={!!errors.mobileNumber}
              helperText={errors.mobileNumber?.message}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="dateOfBirth"
              label="Date of Birth"
              type="date"
              id="dateOfBirth"
              InputLabelProps={{ shrink: true }}
              {...registerForm('dateOfBirth', {
                required: 'Date of birth is required',
                pattern: {
                  value: /^\d{4}-\d{2}-\d{2}$/,
                  message: 'Invalid date format',
                },
              })}
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth?.message}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              {...registerForm('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters',
                },
              })}
              error={!!errors.password}
              helperText={errors.password?.message}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              {...registerForm('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) =>
                  value === watch('password') || 'Passwords do not match',
              })}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Address Information
            </Typography>

            <TextField
              margin="normal"
              required
              fullWidth
              name="street"
              label="Street Address"
              id="street"
              autoComplete="street-address"
              {...registerForm('street', {
                required: 'Street address is required',
              })}
              error={!!errors.street}
              helperText={errors.street?.message}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="city"
                  label="City"
                  id="city"
                  autoComplete="address-level2"
                  {...registerForm('city', {
                    required: 'City is required',
                  })}
                  error={!!errors.city}
                  helperText={errors.city?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="state"
                  label="State"
                  id="state"
                  autoComplete="address-level1"
                  {...registerForm('state', {
                    required: 'State is required',
                  })}
                  error={!!errors.state}
                  helperText={errors.state?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="zipCode"
                  label="ZIP Code"
                  id="zipCode"
                  autoComplete="postal-code"
                  {...registerForm('zipCode', {
                    required: 'ZIP code is required',
                  })}
                  error={!!errors.zipCode}
                  helperText={errors.zipCode?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="country"
                  label="Country"
                  id="country"
                  autoComplete="country"
                  {...registerForm('country', {
                    required: 'Country is required',
                  })}
                  error={!!errors.country}
                  helperText={errors.country?.message}
                />
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              Sign Up
            </Button>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Link component={RouterLink} to="/login" variant="body2">
                  {"Already have an account? Sign In"}
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Register; 